import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Filter,
  X,
  ChevronDown,
  SlidersHorizontal,
  Clock,
  Star,
  Tag,
  FileText,
  Scale,
  Building2,
  Briefcase,
  Heart,
  Home,
  Car,
  Sparkles
} from 'lucide-react';

// Category data
const CATEGORIES = [
  { id: 'all', name: 'Todas', icon: FileText },
  { id: 'civil', name: 'Civil', icon: Scale },
  { id: 'comercial', name: 'Comercial', icon: Building2 },
  { id: 'laboral', name: 'Laboral', icon: Briefcase },
  { id: 'familia', name: 'Familia', icon: Heart },
  { id: 'inmobiliario', name: 'Inmobiliario', icon: Home },
  { id: 'vehicular', name: 'Vehículos', icon: Car }
];

const COMPLEXITY_LEVELS = [
  { id: 'all', name: 'Cualquier complejidad' },
  { id: 'simple', name: 'Simple', description: '5-10 min' },
  { id: 'medium', name: 'Medio', description: '10-20 min' },
  { id: 'complex', name: 'Complejo', description: '20+ min' }
];

const SORT_OPTIONS = [
  { id: 'popular', name: 'Más populares', icon: Star },
  { id: 'recent', name: 'Más recientes', icon: Clock },
  { id: 'name', name: 'Alfabético', icon: Tag },
  { id: 'ai', name: 'Recomendado IA', icon: Sparkles }
];

const AdvancedSearch = ({ 
  templates = [], 
  onFilteredTemplates, 
  initialCategory = 'all',
  className = '' 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [complexity, setComplexity] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => {
    const saved = localStorage.getItem('notaryos_recent_searches');
    return saved ? JSON.parse(saved) : [];
  });

  // Filter and sort templates
  const filteredTemplates = useMemo(() => {
    let result = [...templates];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.name?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query) ||
        t.category?.toLowerCase().includes(query) ||
        t.keywords?.some(k => k.toLowerCase().includes(query))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(t => t.category === selectedCategory);
    }

    // Complexity filter
    if (complexity !== 'all') {
      const complexityMap = {
        simple: (t) => (t.fields?.length || 0) <= 5,
        medium: (t) => (t.fields?.length || 0) > 5 && (t.fields?.length || 0) <= 10,
        complex: (t) => (t.fields?.length || 0) > 10
      };
      result = result.filter(complexityMap[complexity] || (() => true));
    }

    // Sort
    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
        break;
      case 'recent':
        result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      case 'name':
        result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'ai':
        // AI recommendation would use ML in production
        result.sort((a, b) => (b.ai_score || Math.random()) - (a.ai_score || Math.random()));
        break;
    }

    return result;
  }, [templates, searchQuery, selectedCategory, complexity, sortBy]);

  // Update parent with filtered results
  React.useEffect(() => {
    if (onFilteredTemplates) {
      onFilteredTemplates(filteredTemplates);
    }
  }, [filteredTemplates, onFilteredTemplates]);

  // Save search to recent
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() && !recentSearches.includes(query.trim())) {
      const newRecent = [query.trim(), ...recentSearches.slice(0, 4)];
      setRecentSearches(newRecent);
      localStorage.setItem('notaryos_recent_searches', JSON.stringify(newRecent));
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setComplexity('all');
    setSortBy('popular');
  };

  const hasActiveFilters = searchQuery || selectedCategory !== 'all' || complexity !== 'all';

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Main Search Bar */}
      <div className="relative">
        <div className="relative flex items-center">
          <Search className="absolute left-4 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Buscar contratos por nombre, categoría o palabras clave..."
            className="w-full pl-12 pr-32 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
            data-testid="advanced-search-input"
          />
          <div className="absolute right-2 flex items-center space-x-2">
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="text-sm">Filtros</span>
              {hasActiveFilters && (
                <span className="w-2 h-2 bg-blue-500 rounded-full" />
              )}
            </button>
          </div>
        </div>

        {/* Recent Searches Dropdown */}
        {searchQuery === '' && recentSearches.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800 border border-slate-700 rounded-xl shadow-lg z-10 overflow-hidden">
            <div className="p-2">
              <p className="text-xs text-slate-500 px-2 mb-2">Búsquedas recientes</p>
              {recentSearches.map((search, idx) => (
                <button
                  key={idx}
                  onClick={() => setSearchQuery(search)}
                  className="w-full flex items-center space-x-2 px-3 py-2 hover:bg-slate-700 rounded-lg text-left transition-colors"
                >
                  <Clock className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-300">{search}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Category Filters */}
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
              selectedCategory === cat.id
                ? 'bg-blue-500 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'
            }`}
            data-testid={`category-filter-${cat.id}`}
          >
            <cat.icon className="w-4 h-4" />
            <span>{cat.name}</span>
          </button>
        ))}
      </div>

      {/* Advanced Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Complexity Filter */}
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Complejidad</label>
                  <div className="space-y-2">
                    {COMPLEXITY_LEVELS.map((level) => (
                      <button
                        key={level.id}
                        onClick={() => setComplexity(level.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                          complexity === level.id
                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                            : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        <span>{level.name}</span>
                        {level.description && (
                          <span className="text-xs text-slate-500">{level.description}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sort By */}
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Ordenar por</label>
                  <div className="space-y-2">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setSortBy(option.id)}
                        className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          sortBy === option.id
                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                            : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        <option.icon className="w-4 h-4" />
                        <span>{option.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Results Summary */}
                <div className="flex flex-col justify-between">
                  <div>
                    <label className="text-sm text-slate-400 mb-2 block">Resultados</label>
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-white">{filteredTemplates.length}</p>
                      <p className="text-sm text-slate-400">plantillas encontradas</p>
                    </div>
                  </div>
                  
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="mt-4 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                      <span>Limpiar filtros</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Filters Tags */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-500">Filtros activos:</span>
          {searchQuery && (
            <span className="flex items-center space-x-1 px-2 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full">
              <Search className="w-3 h-3" />
              <span>"{searchQuery}"</span>
              <button onClick={() => setSearchQuery('')} className="hover:text-blue-300">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {selectedCategory !== 'all' && (
            <span className="flex items-center space-x-1 px-2 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
              <Tag className="w-3 h-3" />
              <span>{CATEGORIES.find(c => c.id === selectedCategory)?.name}</span>
              <button onClick={() => setSelectedCategory('all')} className="hover:text-green-300">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {complexity !== 'all' && (
            <span className="flex items-center space-x-1 px-2 py-1 bg-amber-500/20 text-amber-400 text-sm rounded-full">
              <Filter className="w-3 h-3" />
              <span>{COMPLEXITY_LEVELS.find(c => c.id === complexity)?.name}</span>
              <button onClick={() => setComplexity('all')} className="hover:text-amber-300">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedSearch;
